// Núcleo que preserva o cursor em campo formatado enquanto se digita. Vendorizado de
// UY3.Front/packages/ui/src/internal/caret-mask.ts (U03). O cursor é guardado como "quantos
// caracteres significativos há de um lado dele", o que a reformatação não invalida.
// Invariante de todo modelo: significant(render(s)) === s.

export interface MaskModel {
  /** Os caracteres significativos de `text`, em ordem; também é o filtro do que o campo aceita. */
  readonly significant: (text: string) => string;
  /** O que o operador vê para esses caracteres. `''` vira `''` para o placeholder aparecer. */
  readonly render: (significant: string) => string;
  /** `left` para documentos; `right` para dinheiro, que entra pela direita. */
  readonly anchor: 'left' | 'right';
}

export interface MaskedText {
  readonly value: string;
  readonly caret: number;
}

/** `#` é vaga; o resto é literal, emitido só quando há um caractere depois dele. */
export function renderTemplate(template: string, significant: string): string {
  let rendered = '';
  let index = 0;
  for (const character of template) {
    if (index >= significant.length) break;
    if (character === '#') {
      rendered += significant.charAt(index);
      index += 1;
    } else {
      rendered += character;
    }
  }
  return rendered;
}

export function templateCapacity(template: string): number {
  return Array.from(template).filter((character) => character === '#').length;
}

function significantCountAt(model: MaskModel, value: string, index: number): number {
  return model.significant(value.slice(0, index)).length;
}

function caretAfter(model: MaskModel, value: string, count: number): number {
  if (count <= 0) {
    let last = 0;
    for (let index = 0; index <= value.length; index += 1) {
      if (significantCountAt(model, value, index) === 0) last = index;
      else break;
    }
    return last;
  }
  for (let index = 0; index <= value.length; index += 1) {
    if (significantCountAt(model, value, index) === count) return index;
  }
  return value.length;
}

function caretBeforeLast(model: MaskModel, value: string, count: number): number {
  const characters = model.significant(value).length;
  const wanted = Math.min(Math.max(characters - count, 0), characters);
  for (let index = value.length; index >= 0; index -= 1) {
    if (significantCountAt(model, value, index) === wanted) return index;
  }
  return value.length;
}

/** Caminho do `change`: reformata o texto que o navegador já alterou e reposiciona o cursor. */
export function formatWithCaret(model: MaskModel, text: string, caret: number): MaskedText {
  const value = model.render(model.significant(text));
  if (model.anchor === 'left') {
    return { value, caret: caretAfter(model, value, model.significant(text.slice(0, caret)).length) };
  }
  return { value, caret: caretBeforeLast(model, value, model.significant(text.slice(caret)).length) };
}

/** Troca o valor inteiro, cursor no fim (colar, assentar no blur). */
export function replaceAll(model: MaskModel, significant: string): MaskedText {
  const value = model.render(significant);
  return { value, caret: value.length };
}

/** Apaga um caractere significativo, nunca um separador. `null` quando não há o que apagar. */
export function deleteOneCharacter(
  model: MaskModel,
  text: string,
  caret: number,
  direction: 'backward' | 'forward',
): MaskedText | null {
  const significant = model.significant(text);
  const before = model.significant(text.slice(0, caret)).length;
  const target = direction === 'backward' ? before - 1 : before;
  if (target < 0 || target >= significant.length) return null;
  const remaining = significant.slice(0, target) + significant.slice(target + 1);
  const value = model.render(remaining);
  if (model.anchor === 'left') return { value, caret: caretAfter(model, value, target) };
  return { value, caret: caretBeforeLast(model, value, significant.length - target - 1) };
}
